import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  // 'dashboard': compact, only shown when there is something to fix.
  // 'page': on Hygiene, also explains an unlinked login.
  variant: 'dashboard' | 'page';
  onShowMine?: () => void;
}

// Personal warning: open tickets the login created or owns whose titles need
// attention. Admins and leaders never see it (admins read the overview,
// leaders are outside the naming engine).
export default function NamingBanner({ variant, onShowMine }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState<number | null>(null);
  const [unlinked, setUnlinked] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'user') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiService.getNamingMe();
        if (cancelled) return;
        setCount(r?.data?.count ?? 0);
        setUnlinked(!!r?.meta?.unlinked);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || user.role !== 'user' || count === null) return null;

  if (unlinked) {
    if (variant === 'dashboard') return null;
    return (
      <div className="mb-5 rounded-2xl bg-gray-50 px-4 py-3 text-sm text-gray-600 ring-1 ring-gray-200/70">
        Your login isn't matched to a Jira account yet, so personal checks are off. Ask an admin to link it; team-wide results below still work.
      </div>
    );
  }

  if (count === 0) return null;

  const go = () => (onShowMine ? onShowMine() : navigate('/hygiene?mine=1'));

  return (
    <div className="mb-5 flex items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200">
      <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-amber-900">
          {count === 1 ? '1 ticket you created needs a clearer title' : `${count} tickets you created need a clearer title`}
        </div>
        {variant === 'page' && (
          <div className="text-xs text-amber-800">Each one shows why and a suggested rewrite. Rename it in Jira and it drops off on the next sync.</div>
        )}
      </div>
      <button
        type="button"
        onClick={go}
        className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 ring-1 ring-inset ring-amber-300 transition-colors hover:bg-amber-100 active:scale-[0.98] motion-reduce:transition-none"
      >
        Show mine
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
